import React, { useState, useEffect } from 'react';
import T from '../../components/common/T';
import { useNavigate, useLocation } from 'react-router-dom'; 
import { useAccounting } from '../../context/AccountingContext';
import { useProjects } from '../../context/ProjectContext'; 
import { useVendors } from '../../context/VendorContext'; 
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useCurrency } from '../../hooks/useCurrency';
import { useCustomFields } from '../../hooks/useCustomFields';
import CustomFieldRenderer from '../../components/common/CustomFieldRenderer';

export default function VendorBillForm() {
    const { createVendorBill } = useAccounting();
    const { projects } = useProjects();
    const { vendors } = useVendors(); 
    const navigate = useNavigate();
    const location = useLocation(); 

    const { activeCurrency } = useCurrency();

    // VendorBillForm is always a NEW record — no id until after save
    const { customData, setCustomData, saveCustomFields } = useCustomFields('accounting', null);

    const [formData, setFormData] = useState({
        vendor_id: '',
        bill_number: '',
        project_id: '',
        purchase_order_id: '', 
        bill_date: new Date().toISOString().split('T')[0],
        due_date: '',
        total_amount: '',
        notes: ''
    });

    useEffect(() => {
        if (location.state?.poData) {
            const po = location.state.poData;
            setFormData(prev => ({
                ...prev,
                vendor_id: po.vendorId ? String(po.vendorId) : '', 
                project_id: po.projectId ? String(po.projectId) : '', 
                purchase_order_id: po.id, 
                total_amount: po.totalPrice, 
                notes: `Generated from PO #${po.poNumber}`
            }));
        }
    }, [location.state]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.vendor_id || !formData.total_amount) return alert("Please fill required fields");
        
        // Capture the saved bill to get its ID for custom fields
        const savedBill = await createVendorBill(formData);

        // Save custom fields BEFORE navigating away
        if (savedBill?.data?.id) {
            await saveCustomFields(savedBill.data.id);
        }

        navigate('/admin/accounting/bills'); 
    };

    return (
        <div className="p-8 bg-gray-50 dark:bg-[#0d1117] min-h-screen font-sans transition-colors">
            <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-4 mb-6">
                    <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-800 dark:text-slate-400 dark:hover:text-white transition">
                        <ArrowLeftIcon className="w-6 h-6" />
                    </button>
                    <h1 className="text-base font-bold text-gray-900 dark:text-white">
                        {formData.purchase_order_id ? "Convert PO to Bill" : "Record Vendor Bill"}
                    </h1>
                </div>

                <form onSubmit={handleSubmit} className="bg-white dark:bg-[#161b22] rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d] overflow-hidden transition-colors">
                    <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-1"><T>Vendor *</T></label>
                                <select 
                                    className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2.5 bg-white dark:bg-[#21262d] text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-slate-800 outline-none"
                                    value={formData.vendor_id}
                                    onChange={e => setFormData({...formData, vendor_id: e.target.value})}
                                    required
                                    disabled={!!formData.purchase_order_id} 
                                >
                                    <option value=""><T>Select Vendor...</T></option>
                                    {vendors.map(v => (
                                        <option key={v.id} value={String(v.id)}>{v.vendor_name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-1"><T>Project Link</T></label>
                                <select 
                                    className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2.5 bg-white dark:bg-[#21262d] text-gray-900 dark:text-white outline-none"
                                    value={formData.project_id}
                                    onChange={e => setFormData({...formData, project_id: e.target.value})}
                                >
                                    <option value=""><T>General (No Project)</T></option>
                                    {projects.map(p => (
                                        <option key={p.id} value={String(p.id)}>{p.name}</option>
                                    ))}
                                </select>
                            </div>
                             <div>
                                <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-1"><T>Vendor Invoice # *</T></label>
                                <input 
                                    type="text" 
                                    className="w-full border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-900 dark:text-white rounded-lg p-2.5 outline-none" 
                                    placeholder="e.g. INV-998877"
                                    value={formData.bill_number}
                                    onChange={e => setFormData({...formData, bill_number: e.target.value})}
                                    required
                                />
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-1"><T>Bill Date *</T></label>
                                <input 
                                    type="date" 
                                    className="w-full border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-900 dark:text-white rounded-lg p-2.5 outline-none" 
                                    value={formData.bill_date}
                                    onChange={e => setFormData({...formData, bill_date: e.target.value})}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-1"><T>Due Date *</T></label>
                                <input 
                                    type="date" 
                                    className="w-full border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-900 dark:text-white rounded-lg p-2.5 outline-none" 
                                    value={formData.due_date}
                                    onChange={e => setFormData({...formData, due_date: e.target.value})}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-1">
                                    <T>Total Amount</T> ({activeCurrency?.symbol || '$'}) *
                                </label>
                                <input 
                                    type="number" 
                                    className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2.5 font-bold text-gray-900 dark:text-white bg-gray-50 dark:bg-[#21262d] outline-none" 
                                    value={formData.total_amount}
                                    onChange={e => setFormData({...formData, total_amount: e.target.value})}
                                    required
                                />
                            </div>
                        </div>

                        <div className="col-span-1 md:col-span-2">
                             <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-1"><T>Notes / Description</T></label>
                             <textarea 
                                rows="3"
                                className="w-full border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-900 dark:text-white rounded-lg p-2.5 outline-none"
                                placeholder="Details about this bill..."
                                value={formData.notes}
                                onChange={e => setFormData({...formData, notes: e.target.value})}
                             />
                        </div>
                    </div>

                    {/* Custom Fields */}
                    <div className="px-8 pb-4">
                        <CustomFieldRenderer module="accounting" customData={customData} onChange={setCustomData} />
                    </div>

                    <div className="p-6 bg-gray-50 dark:bg-[#161b22] flex justify-end gap-4 border-t border-gray-200 dark:border-[#30363d] transition-colors">
                        <button type="button" onClick={() => navigate(-1)} className="px-6 py-2.5 text-gray-600 dark:text-slate-300 font-bold hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition">
                            Cancel
                        </button>
                        <button type="submit" className="px-8 py-2.5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg shadow-md transition">
                            {formData.purchase_order_id ? "Post PO Bill" : "Save Bill"}
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
}